'use client';

import { c as _c } from "react/compiler-runtime";
import { Calendar, CalendarSolid, Clock, MapPin, MapPinSolid, Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { Tag as RAC_Tag, TagGroup as RAC_TagGroup, TagList as RAC_TagList, Text } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Label } from '~/components/Label';
import styles from './TagGroup.module.css';
import type { TagGroupProps, TagProps } from './TagGroup.types';
function TagGroup(t0) {
  const $ = _c(25);
  let children;
  let description;
  let errorMessage;
  let items;
  let label;
  let props;
  let renderEmptyState;
  if ($[0] !== t0) {
    ({
      label,
      description,
      errorMessage,
      items,
      children,
      renderEmptyState,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = description;
    $[3] = errorMessage;
    $[4] = items;
    $[5] = label;
    $[6] = props;
    $[7] = renderEmptyState;
  } else {
    children = $[1];
    description = $[2];
    errorMessage = $[3];
    items = $[4];
    label = $[5];
    props = $[6];
    renderEmptyState = $[7];
  }
  let t1;
  if ($[8] !== label) {
    t1 = <Label isControl={true} id="sth dynamic">{label}</Label>;
    $[8] = label;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== children || $[11] !== items || $[12] !== renderEmptyState) {
    t2 = <RAC_TagList className={styles.tagList} items={items} renderEmptyState={renderEmptyState}>{children}</RAC_TagList>;
    $[10] = children;
    $[11] = items;
    $[12] = renderEmptyState;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== description) {
    t3 = description && <Text slot="description">{description}</Text>;
    $[14] = description;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== errorMessage) {
    t4 = errorMessage && <Text slot="errorMessage">{errorMessage}</Text>;
    $[16] = errorMessage;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== label || $[19] !== props || $[20] !== t1 || $[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t5 = <RAC_TagGroup aria-label={label} className={styles.tagGroup} {...props}>{t1}{t2}{t3}{t4}</RAC_TagGroup>;
    $[18] = label;
    $[19] = props;
    $[20] = t1;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
}
function Tag(t0) {
  const $ = _c(21);
  let children;
  let mode;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      mode,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = mode;
    $[3] = props;
  } else {
    children = $[1];
    mode = $[2];
    props = $[3];
  }
  const t = useTranslations("aria");
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      MapPin: <MapPin decorative={true} size="large" color="black" />,
      MapPinSolid: <MapPinSolid decorative={true} color="white" size="large" />,
      Calendar: <Calendar decorative={true} size="small" color="black" />,
      CalendarSolid: <CalendarSolid decorative={true} color="white" size="small" />,
      Clock: <Clock decorative={true} size="small" color="black" />,
      ClockSolid: <Clock decorative={true} color="white" size="small" />
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const icons = t1;
  const icon = props?.icon;
  const isClear = props?.variant === "clear";
  const textValue = typeof children === "string" ? children : props.textValue;
  const t2 = props.id;
  const t3 = props["aria-label"];
  const t4 = props.variant === "secondary";
  const t5 = mode === "unselectable";
  let t6;
  if ($[5] !== isClear || $[6] !== t4 || $[7] !== t5) {
    t6 = clsx(styles.tag, {
      [styles.secondary]: t4,
      [styles.tagUnselectable]: t5,
      [styles.tagClear]: isClear
    });
    $[5] = isClear;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== children || $[10] !== icon || $[11] !== props.handleRemove || $[12] !== t || $[13] !== textValue) {
    t7 = t8 => {
      const {
        allowsRemoving,
        isSelected,
        selectionMode
      } = t8;
      let iconWithFill;
      if (icon && !isSelected) {
        iconWithFill = icons[icon];
      }
      if (icon && isSelected) {
        iconWithFill = icons[`${icon}Solid`];
      }
      return <>{iconWithFill}{children}{(allowsRemoving || props.handleRemove) && (isSelected || selectionMode === "none") && <Button slot="remove" variant="icon" ariaLabel={textValue ? t("delete_tag", {
          tag: textValue
        }) : t("delete")}><Xmark decorative={true} /></Button>}</>;
    };
    $[9] = children;
    $[10] = icon;
    $[11] = props.handleRemove;
    $[12] = t;
    $[13] = textValue;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== props || $[16] !== t3 || $[17] !== t6 || $[18] !== t7 || $[19] !== textValue) {
    t8 = <RAC_Tag key={t2} aria-label={t3} className={t6} textValue={textValue} {...props}>{t7}</RAC_Tag>;
    $[15] = props;
    $[16] = t3;
    $[17] = t6;
    $[18] = t7;
    $[19] = textValue;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
export { TagGroup, Tag };